import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/common/DataTable";
import { Edit2, Play, Pause, Search, Package, CheckCircle2 } from "lucide-react";
import { useClientLicenses } from "@/hooks/useClientLicenses";
import { useModulePrices } from "@/hooks/useModulePrices";
import { EditClientLicenseDialog } from "./EditClientLicenseDialog";
import { ClientLicense } from "@/types/license";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const ClientLicensesTab = () => {
  const { 
    clientLicenses, 
    isLoading, 
    suspendClientLicense, 
    activateClientLicense,
    isSuspendingLicense,
    isActivatingLicense
  } = useClientLicenses();
  const { modulePrices } = useModulePrices();
  const [editingLicense, setEditingLicense] = useState<ClientLicense | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all client_modules
  const { data: allClientModules = [] } = useQuery({
    queryKey: ['all-client-modules-for-licenses'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('is_active', true);
      if (error) throw error;
      return data;
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'trial': return 'bg-blue-500';
      case 'suspended': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Ativa';
      case 'trial': return 'Trial';
      case 'suspended': return 'Suspensa';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  const getClientModules = (clientId: string) => {
    return allClientModules.filter(cm => cm.client_id === clientId);
  };

  const calculateCost = (license: ClientLicense) => {
    const modules = getClientModules(license.client_id);
    const moduleCost = modules.reduce((sum, cm) => {
      const price = modulePrices.find(p => p.code === cm.module_code);
      return sum + (price?.price || 0);
    }, 0);
    const seats = license.contracted_seats || 1;
    if (license.billing_cycle === 'yearly') {
      return moduleCost * seats * 10; // annual cost
    }
    return moduleCost * seats;
  };

  const columns = [
    {
      key: "client",
      header: "Cliente",
      render: (license: ClientLicense) => (
        <div>
          <div className="font-medium">{license.client?.name}</div>
          <div className="text-sm text-muted-foreground">
            {license.contracted_seats || 1} licença(s)
          </div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (license: ClientLicense) => (
        <div className="flex items-center gap-2">
          <Badge className={`${getStatusColor(license.status)} text-white`}>
            {getStatusLabel(license.status)}
          </Badge>
          {license.stripe_subscription_id && (
            <Badge variant="outline" className="text-xs border-green-500 text-green-600">
              Stripe
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "modules",
      header: "Módulos Ativos",
      render: (license: ClientLicense) => {
        const modules = getClientModules(license.client_id);
        if (modules.length === 0) return <span className="text-muted-foreground">Nenhum</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {modules.slice(0, 3).map(cm => {
              const mod = modulePrices.find(p => p.code === cm.module_code);
              return (
                <Badge key={cm.id} variant="outline" className="text-xs">
                  {mod?.name || cm.module_code}
                </Badge>
              );
            })}
            {modules.length > 3 && (
              <Badge variant="secondary" className="text-xs">+{modules.length - 3}</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "cost",
      header: "Custo",
      render: (license: ClientLicense) => {
        const cost = calculateCost(license);
        return (
          <div>
            <div className="font-medium">
              R$ {cost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </div>
            <div className="text-sm text-muted-foreground">
              {license.billing_cycle === 'monthly' ? '/mês' : '/ano'}
            </div>
          </div>
        );
      },
    },
    {
      key: "period",
      header: "Período",
      render: (license: ClientLicense) => (
        <div className="text-sm space-y-1">
          <div>Início: {format(new Date(license.start_date), 'dd/MM/yyyy', { locale: ptBR })}</div>
          {license.end_date && (
            <div>Fim: {format(new Date(license.end_date), 'dd/MM/yyyy', { locale: ptBR })}</div>
          )}
          {license.trial_end_date && license.status === 'trial' && (
            <div className="text-blue-600">
              Trial até: {format(new Date(license.trial_end_date), 'dd/MM/yyyy', { locale: ptBR })}
            </div>
          )}
          {license.stripe_subscription_id && license.status === 'active' && (
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>Assinatura ativa</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "Ações",
      render: (license: ClientLicense) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditingLicense(license)}>
            <Edit2 className="h-4 w-4" />
          </Button>
          {license.status === 'active' ? (
            <Button variant="ghost" size="sm" onClick={() => suspendClientLicense(license.id)} disabled={isSuspendingLicense}>
              <Pause className="h-4 w-4" />
            </Button>
          ) : license.status === 'suspended' ? (
            <Button variant="ghost" size="sm" onClick={() => activateClientLicense(license.id)} disabled={isActivatingLicense}>
              <Play className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const filteredLicenses = clientLicenses.filter((license) =>
    license.client?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>Filtros</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Assinaturas de Clientes</CardTitle></CardHeader>
        <CardContent>
          <DataTable columns={columns} data={filteredLicenses} />
        </CardContent>
      </Card>

      <EditClientLicenseDialog
        license={editingLicense}
        open={!!editingLicense}
        onOpenChange={(open) => !open && setEditingLicense(null)}
      />
    </div>
  );
};
